import type {
  FunctionComponent,
  HTMLProps,
  ReactElement,
  ReactNode,
} from 'react'
import React, { cloneElement, isValidElement } from 'react'

import { useId } from '@react-aria/utils'
import classnames from 'classnames'

import { Label } from '../Label'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import { TooltipWithButton } from '../Tooltip/'
import styles from './FormField.module.scss'

export const FormField: FunctionComponent<Props> = ({
  children,
  label,
  labelFor,
  errorMsg,
  tooltip,
  fieldInfo = '',
  required = false,
  className = '',
  showRequiredText = true,
  ...rest
}) => {
  const fieldInfoId = useId()

  const infoLabel = required ? '(required)' : '(optional)'

  const labelText = showRequiredText ? `${label} ${infoLabel}` : label

  const mappedChild =
    fieldInfo && isValidElement(children)
      ? cloneElement(children as ReactElement, {
          'aria-describedby': fieldInfoId,
        })
      : children

  return (
    <div className={classnames(styles.formField, className)} {...rest}>
      <div className={styles.formFieldWrapper}>
        <div className={styles.formFieldLabelWrapper}>
          {labelText && (
            <Label
              className={styles.formFieldLabel}
              htmlFor={labelFor}
              labelText={labelText}
            />
          )}
          {tooltip && (
            <TooltipWithButton
              ariaLabel="tooltip"
              contents={tooltip}
              className={styles.tooltip}
            />
          )}
        </div>
        {fieldInfo && (
          <TypographyV2
            textStyle="sb-text-interface-body-small-regular"
            className={styles.formFieldInfo}
            id={fieldInfoId}
          >
            {fieldInfo}
          </TypographyV2>
        )}
        {mappedChild}
      </div>

      {errorMsg && (
        <TypographyV2
          role="alert"
          textStyle="sb-text-interface-body-small-regular"
          className={styles.formFieldInputValidationError}
          data-testid="input-validation-error"
        >
          {errorMsg}
        </TypographyV2>
      )}
    </div>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Provided form element for field.
   */
  children: ReactNode
  /**
   * Optional text label displayed above input linked via htmlFor attribute.
   */
  label?: string
  /**
   * Optional htmlFor value to link input to label.
   */
  labelFor?: string
  /**
   * Optional required value that appends the attribute.
   */
  required?: boolean
  /**
   * Optional field info/help text displayed below input element.
   */
  fieldInfo?: string
  /**
   * Error message when provided displays under input element.
   */
  errorMsg?: string
  /**
   * Optional CSS class appended to field container for additional styling.
   */
  className?: string
  /**
   * Optional tooltip text for info icon.
   */
  tooltip?: string
  /**
   * Show required text
   */
  showRequiredText?: boolean
} & HTMLProps<HTMLDivElement>
